/**
 * Event-class thresholds. Intensity (growing / cooling) stays in status.ts.
 * Confirmed is locked behind a direct thermal group — context cannot get you here.
 */

import { MTG_INGEST_MIN_FRP_MW } from "./mtg-list";
import { IMPORTANT_FRP_MW, isImportantEvent } from "./status";
import type { EventStatus, Incident } from "./types";

/** Distinct UTC days of modest recurring heat before we treat it as a stack. */
export const STANDING_MIN_DAYS = 4;
/**
 * Pixel floor so a 4-day peat / small wildfire (~30 looks) stays on Suspected.
 * Live steel (Gent, Dunkirk, Salzgitter) sits at 200–600 pixels over the same window.
 */
export const STANDING_MIN_PIXELS = 80;

export const EVENT_THRESHOLDS = {
  candidate: 0.02,
  suspected: 0.2,
  likely: 0.7,
  confirmed: 0.97,
} as const;

/** Polar look below this is a pinprick — used by the scorer and the D1-lag clamp. */
export const POLAR_NOMINAL_MW = 8;
export const POLAR_STRONG_MW = 30;

/**
 * Hidden from the public map until Suspected. Candidate is engine-internal.
 * Observed is two qualifying platforms — stacked same-sat looks cannot buy that class.
 * Likely / Observed require a thermal group so weather weights cannot get there.
 */
export function classifyEventStatus(
  fireProbability: number,
  hasDirectThermal: boolean,
  independentSensors = 0,
): EventStatus {
  if (
    fireProbability > EVENT_THRESHOLDS.confirmed &&
    hasDirectThermal &&
    independentSensors >= 2
  ) {
    return "confirmed";
  }
  if (fireProbability > EVENT_THRESHOLDS.likely && hasDirectThermal) return "likely";
  if (fireProbability > EVENT_THRESHOLDS.suspected) return "suspected";
  if (fireProbability > EVENT_THRESHOLDS.candidate) return "candidate";
  return "none";
}

export type EventClass = "hidden" | "suspected" | "likely" | "observed";

/** One exclusive map view. Default is fires. */
export type MapView = "fires" | "suspected" | "campaigns" | "rising" | "new" | "fine";

/** How-sure slice of the public set. */
export type ShowMode = "fires" | "suspected";

/** Map / filter mapping for the Fires · Suspected strip. */
export function eventClass(status: EventStatus): EventClass {
  if (status === "confirmed") return "observed";
  if (status === "likely") return "likely";
  if (status === "suspected") return "suspected";
  return "hidden";
}

/**
 * Unscored / candidate / none stay hidden. Null is not two platforms.
 */
export function eventClassOf(status: EventStatus | null | undefined): EventClass {
  if (status == null) return "hidden";
  return eventClass(status);
}

export function isPublicClass(cls: EventClass): boolean {
  return cls === "observed" || cls === "likely" || cls === "suspected";
}

/** Observed + Likely — “we would tell a neighbor.” */
export function isFireClass(cls: EventClass): boolean {
  return cls === "observed" || cls === "likely";
}

export function matchesShowMode(cls: EventClass, mode: ShowMode): boolean {
  if (!isPublicClass(cls)) return false;
  if (mode === "suspected") return cls === "suspected";
  return isFireClass(cls);
}

export function eventClassLabel(cls: EventClass): string {
  if (cls === "observed") return "Observed";
  if (cls === "likely") return "Likely";
  if (cls === "suspected") return "Suspected";
  return "Hidden";
}

/**
 * D1 can still hold an old class until the next ingest rewrite.
 * Same size floor as the scorer — not a second taxonomy.
 */
export function looksLikeUnconfirmedBlip(
  inc: Pick<Incident, "detection_count" | "frp_mw" | "last_seen" | "sources" | "event_status">,
  nowMs: number,
): boolean {
  const n = inc.detection_count ?? 0;
  const frp = inc.frp_mw ?? 0;
  const ageH = (nowMs - Date.parse(inc.last_seen)) / 3_600_000;
  if (!Number.isFinite(ageH)) return false;
  const sources = inc.sources ?? [];
  // Observed plus a second family (MTG / S3) can die down without vanishing off Fires.
  // Two VIIRS birds on a 1 MW stack still hide — that is the Differdange over-call.
  const secondFamily = sources.some((s) => s === "S3" || s === "MTG");
  if (inc.event_status === "confirmed" && secondFamily) return false;
  // Small + under the polar floor — hide even if D1 still says Observed.
  if (n <= 5 && frp < POLAR_NOMINAL_MW) return true;
  if (inc.event_status === "confirmed") return false;
  if (sources.length >= 2) return false;
  // One quiet polar cycle on a small unconfirmed look — matches staleUnconfirmed.
  if (ageH >= 12 && n <= 3 && frp < POLAR_STRONG_MW) return true;
  return false;
}

/**
 * Multi-day modest heat that never stamped Important — Ghent steel, not High Fens.
 * Uses incident fields only so /api/fires can clamp before the next ingest rewrite.
 *
 * Keep: a fire still jumping toward campaign size, or MTG still seeing ≥20 MW
 * (geostationary only stores those). Hide: polar-only furnaces that drip for days.
 */
export function looksLikeStandingStack(
  inc: Pick<Incident, "first_seen" | "last_seen" | "detection_count" | "frp_mw" | "last_important_at"> &
    Partial<Pick<Incident, "status" | "sources" | "thermal_last_seen">>,
  nowMs = Date.now(),
): boolean {
  // Same clock as Major events — leftover looks in the stamp window still count.
  // A one-time 50 MW look must not hide-exempt a furnace once that hold ends.
  if (isImportantEvent({ ...inc, status: inc.status ?? "cooling" }, nowMs)) return false;
  const firstDay = inc.first_seen.slice(0, 10);
  // S3 can refresh last_seen on a later calendar day — do not let that push a 3-day fire over the cut.
  const lastClock = inc.thermal_last_seen ?? inc.last_seen;
  const lastDay = lastClock.slice(0, 10);
  let daySpan = (Date.parse(lastDay) - Date.parse(firstDay)) / 86_400_000 + 1;
  if (!inc.thermal_last_seen && (inc.sources ?? []).includes("S3")) daySpan -= 1;
  if (!Number.isFinite(daySpan) || daySpan < STANDING_MIN_DAYS) return false;
  if ((inc.detection_count ?? 0) < STANDING_MIN_PIXELS) return false;
  const frp = inc.frp_mw ?? 0;
  if (frp >= IMPORTANT_FRP_MW) return false;
  // A 4-day fire climbing through 20–40 MW must not vanish on the day it jumps.
  if (inc.status === "rapidly_intensifying") return false;
  // MTG in sources + current FRP still at the ingest floor = live fire-scale heat.
  if ((inc.sources ?? []).includes("MTG") && frp >= MTG_INGEST_MIN_FRP_MW) return false;
  return true;
}

/** Clamp an over-called stored score so the API / map match the cautious engine. */
export function cautionDisplay(inc: Incident, nowMs: number): Incident {
  if (looksLikeStandingStack(inc, nowMs)) {
    // Hidden on Fires / Suspected / Major. High Sensitivity still lists the row.
    return {
      ...inc,
      event_status: "candidate",
      fire_probability: Math.min(inc.fire_probability ?? 1, EVENT_THRESHOLDS.suspected - 0.01),
    };
  }
  if (!looksLikeUnconfirmedBlip(inc, nowMs)) return inc;
  return {
    ...inc,
    event_status: "candidate",
    fire_probability: Math.min(inc.fire_probability ?? 1, EVENT_THRESHOLDS.suspected - 0.01),
  };
}
